/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/audit-log-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { AUDIT_LOG_COLUMNS } from '@pages/admin/audit-log/grid.data';
import DetailGrid from './detail-grid';
import { useTranslation } from 'hooks';
import { AuditActionType, AuditServiceType } from 'types/graphql-global-types';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { getPrefixByService } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes, panelsAndTablesHistory } from '@queries/generated/audit.generated';
import { AuditLogFields } from '@queries/generated/fragments.generated';
import { userStudies as UserStudies } from '@queries/generated/studies.generated';

interface AuditLogGridProps {
    items: any;
    userStudies: UserStudies['userStudies'];
    filteredLogItems: AuditLogFields[];
    orgId: any;
    studies: any;
}

const isSystemUser = (email: string | null) => email === 'System';

const AuditLogGrid = ({ items, userStudies, filteredLogItems, orgId, studies }: AuditLogGridProps) => {
    const { t } = useTranslation();
    const [savedData, setSavedData] = useState({} as any);
    const [data, setData] = useState<AuditLogFields[]>([]);
    const [additionalData, setAdditionalData] = useState<panelsAndTablesHistory | null>(null);

    const { data: prefixesData } = useQuery<auditRecordIdPrefixes>(AUDIT_RECORD_ID_PREFIXES, {
        fetchPolicy: 'cache-first',
    });

    const getStudyName = (studyId: string) => {
        const study = userStudies?.find((s) => s?.id === studyId);
        if (study) return study.studyNumber ?? study.shortName;

        return '';
    };

    useEffect(() => {
        if (filteredLogItems && userStudies) {
            const array = [...filteredLogItems].map((item: AuditLogFields) => {
                const isSystem = isSystemUser(item.userEmail);
                const email = isSystem ? null : item.userEmail;
                const recordId = isSystem ? null : item.recordId;
                return {
                    ...item,
                    userEmail: email,
                    studyName: getStudyName(item.studyId),
                    recordId: recordId ? +recordId : recordId,
                    reason: item.reason ?? '',
                    data: item.data,
                    _entityType: t(`auditLog:${item.data}`),
                    _itemId:
                        item.action === AuditActionType.DATA_EXPORT && !item.itemRecordId
                            ? null
                            : `${getPrefixByService(
                                  prefixesData?.auditRecordIdPrefixes ?? [],
                                  item.originService || AuditServiceType.CORE,
                              )}${item.itemRecordId}`,
                    _translatedAction: t(`auditLog:${item.action}`),
                };
            });
            setData(array);
        }
    }, [filteredLogItems, userStudies]);

    const setSavedItem = (id: number | string, item: any) => {
        setSavedData({
            ...savedData,
            [id]: item,
        });
    };

    return (
        <ExpandableGrid
            customKey="audit-log-grid"
            id="audit-log"
            groupable={false}
            showPager
            scrollable="scrollable"
            reorderable
            pageable={false}
            addScroll
            resizable
            items={data}
            options={{
                allowExcelExport: false,
                allowExpandCollapseAll: false,
                useFilterChips: true,
            }}
            detail={(record: any) => (
                <DetailGrid
                    record={record}
                    items={items}
                    savedItem={savedData[record.itemId]}
                    setSavedItem={setSavedItem}
                    orgId={orgId}
                    setAdditionalData={setAdditionalData}
                    additionalData={additionalData ?? studies?.find((item: any) => item.id === record.studyId)}
                />
            )}
            columns={AUDIT_LOG_COLUMNS(t)}
        />
    );
};

export default React.memo(AuditLogGrid);
